// Airports list: search and filters, bulk status, the table, and the create / edit drawer.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import {
  airportCountries,
  bulkAirportStatus,
  createAirport,
  deleteAirport,
  listAirports,
  updateAirport,
} from '../../api/endpoints';
import type { Airport, AirportStatus } from '../../api/types';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';
import { StatusTag, homebaseAffectedText } from './airportBits';
import { AirportForm } from './AirportForm';
import { createdZoneNotice, guessText } from '../../data/airportTime';

const { Title, Text } = Typography;
const PAGE_SIZE = 10; // SHAR-204

export function AirportsListPage() {
  const [q, setQ] = useState('');
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<AirportStatus | undefined>(undefined);
  // SHAR-1192 — only the airports whose zone SHAR-1188 had to guess, to review them.
  const [guessedOnly, setGuessedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Airport | null>(null);
  const [form] = Form.useForm();
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['airports', q, country, status, page, guessedOnly],
    queryFn: () =>
      listAirports({
        q: q || undefined,
        country,
        status,
        ...(guessedOnly ? { timezoneGuessed: true } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const countries = useQuery({ queryKey: ['airport-countries'], queryFn: airportCountries });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['airports'] });
    qc.invalidateQueries({ queryKey: ['airport-countries'] });
  };

  const createMut = useMutation({
    mutationFn: createAirport,
    onSuccess: (row) => {
      message.success('Airport created');
      // SHAR-1192 (2b) — a guessed zone is on the review list: say which zone, and why
      const notice = createdZoneNotice(row);
      if (notice) message.warning(notice, 8);
      setDrawerOpen(false);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Create failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => updateAirport(id, body),
    onSuccess: () => {
      message.success('Airport updated');
      setDrawerOpen(false);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteAirport,
    onSuccess: () => {
      message.success('Airport deleted');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });
  const bulkMut = useMutation({
    mutationFn: ({ ids, s }: { ids: string[]; s: AirportStatus }) => bulkAirportStatus(ids, s),
    onSuccess: (r) => {
      message.success(`Updated ${r.updated} airport${r.updated === 1 ? '' : 's'}`);
      setSelectedKeys([]);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Bulk update failed'),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' }); // SHAR-205 default
    setDrawerOpen(true);
  };
  const openEdit = (a: Airport) => {
    setEditing(a);
    form.setFieldsValue({ ...a, timezoneChecked: false });
    setDrawerOpen(true);
  };

  // Regression 28 Sep — one submit at a time (a double click sent a 2nd POST).
  const submitGuard = useSingleSubmit();
  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    const body = {
      icaoCode: (v.icaoCode || '').toUpperCase(),
      iataCode: v.iataCode ? String(v.iataCode).toUpperCase() : null,
      name: v.name,
      city: v.city || null,
      countryCode: (v.countryCode || '').toUpperCase(),
      isoRegion: v.isoRegion ? String(v.isoRegion).toUpperCase() : null,
      lat: v.lat ?? null,
      lng: v.lng ?? null,
      // SHAR-1188 — left out when empty: the API then keeps (or, on create, guesses) the zone.
      ...(v.timezone ? { timezone: v.timezone } : {}),
      // SHAR-1192 — only sent when ticked, so an older API never sees it.
      ...(v.timezoneChecked ? { timezoneChecked: true } : {}),
      status: v.status,
      fboName: v.fboName || null,
      fboAddress: v.fboAddress || null,
      fboPhone: v.fboPhone || null,
      fboEmail: v.fboEmail || null,
      runwayLengthFt: v.runwayLengthFt ?? null,
      openingHours: v.openingHours || null,
      generalNotes: v.generalNotes || null,
    };
    if (editing) await updateMut.mutateAsync({ id: editing.id, body });
    else await createMut.mutateAsync(body as any);
  });

  const countryOptions = useMemo(
    () =>
      (countries.data || []).map((c) => ({
        value: c.code,
        label: `${c.name} (${c.code})`,
      })),
    [countries.data],
  );

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Airports</Title>
            <Text type="secondary">
              Only active airports are offered in aircraft, admin booking and mobile lookups.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New airport
          </Button>
        </div>

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name or ICAO"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              allowClear
              style={{ width: 280 }}
            />
            <Select
              placeholder="ISO country"
              allowClear
              showSearch
              // SHAR-455 — prefix match (typing "un" -> United..., not Brunei/
              // Burundi/Hungary), not the default loose substring match.
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .startsWith(input.toLowerCase())
              }
              value={country}
              onChange={(v) => {
                setCountry(v);
                setPage(1);
              }}
              options={countryOptions}
              style={{ width: 220 }}
            />
            <Select
              placeholder="Status"
              allowClear
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                // SHAR-498/500/501 — removed airports are hidden from the
                // default list (soft delete), so this is the only way to see
                // them; the backend already accepts the filter.
                { value: 'removed', label: 'Removed' },
              ]}
              style={{ width: 160 }}
            />
            <Checkbox
              checked={guessedOnly}
              onChange={(e) => {
                setGuessedOnly(e.target.checked);
                setPage(1);
              }}
            >
              Time zone to check
            </Checkbox>
          </Space>

          {/* SHAR-207 — bulk status bar (shown while rows are selected). */}
          {selectedKeys.length > 0 && (
            <Space style={{ marginBottom: 16 }}>
              <Text strong>{selectedKeys.length} selected</Text>
              <Button
                onClick={() =>
                  bulkMut.mutate({ ids: selectedKeys as string[], s: 'active' })
                }
                loading={bulkMut.isPending}
              >
                Set Active
              </Button>
              <Button
                onClick={() =>
                  bulkMut.mutate({ ids: selectedKeys as string[], s: 'inactive' })
                }
                loading={bulkMut.isPending}
              >
                Set Inactive
              </Button>
              <Button type="link" onClick={() => setSelectedKeys([])}>
                Clear selection
              </Button>
            </Space>
          )}

          <Table<Airport>
            rowKey="id"
            loading={list.isLoading}
            dataSource={list.data?.data || []}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: setSelectedKeys,
              preserveSelectedRowKeys: true, // SHAR-207 — persist across pages
            }}
            pagination={{
              current: page,
              total: list.data?.total || 0,
              pageSize: PAGE_SIZE,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (t) => `${t} airports`,
            }}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
              { title: 'ICAO', dataIndex: 'icaoCode', key: 'icaoCode', width: 90 },
              { title: 'ISO country', dataIndex: 'countryCode', key: 'countryCode', width: 110 },
              {
                // SHAR-1192 — the zone, flagged when it was guessed and nobody has checked it yet.
                title: 'Time zone',
                key: 'timezone',
                width: 230,
                render: (_, r) => (
                  <Space size={4} wrap>
                    <span>{r.timezone ?? '—'}</span>
                    {r.timezoneGuessed ? (
                      <Tooltip title={guessText(r.timezoneGuess)}>
                        <Tag color="orange">Guessed</Tag>
                      </Tooltip>
                    ) : null}
                  </Space>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (s: AirportStatus) => <StatusTag status={s} />,
              },
              {
                title: 'Runway length (ft)',
                dataIndex: 'runwayLengthFt',
                key: 'runwayLengthFt',
                width: 150,
                render: (v: number | null) => (v ?? '—'),
              },
              {
                title: '',
                key: 'actions',
                width: 110,
                render: (_, r) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    <Popconfirm
                      title="Delete this airport?"
                      description={homebaseAffectedText(r.homebaseAircraftCount)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteMut.mutate(r.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      <Drawer
        title={editing ? `Edit ${editing.icaoCode}` : 'Create airport'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={460}
        extra={
          <Space>
            {/* SHAR-456 — delete option on the edit screen too (not only the
                list row), confirmed via a pop-up. */}
            {editing ? (
              <Popconfirm
                title="Delete this airport?"
                description={homebaseAffectedText(editing.homebaseAircraftCount)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => {
                  deleteMut.mutate(editing.id);
                  setDrawerOpen(false);
                }}
              >
                <Button danger loading={deleteMut.isPending} icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            ) : null}
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            {editing ? (
              // SHAR-208 — update confirmed via a pop-up.
              <Popconfirm title="Save changes to this airport?" okText="Save" onConfirm={submit}>
                <Button type="primary" loading={submitGuard.busy || updateMut.isPending}>
                  Save
                </Button>
              </Popconfirm>
            ) : (
              <Button type="primary" loading={submitGuard.busy || createMut.isPending} onClick={submit}>
                Create
              </Button>
            )}
          </Space>
        }
      >
        <AirportForm form={form} guessed={editing?.timezoneGuessed ? editing.timezoneGuess ?? '' : null} />
      </Drawer>
    </>
  );
}
